import React, { createContext, useContext, useState } from "react";

const STORAGE_KEY = "mesvida_cookie_consent_v1";
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_MS = 24 * 30 * 24 * 60 * 60 * 1000;

const defaultPreferences = {
  necessary: true,
  analytics: false,
  externalMedia: false,
};

const ConsentContext = createContext(null);

const readStoredConsent = () => {
  if (typeof window === "undefined") return null;

  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (stored?.version !== CONSENT_VERSION || !stored.savedAt) return null;
    if (Date.now() - new Date(stored.savedAt).getTime() > CONSENT_MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return {
      necessary: true,
      analytics: Boolean(stored.analytics),
      externalMedia: Boolean(stored.externalMedia),
    };
  } catch {
    return null;
  }
};

export const ConsentProvider = ({ children }) => {
  const storedConsent = readStoredConsent();
  const [preferences, setPreferences] = useState(
    storedConsent || defaultPreferences
  );
  const [hasDecision, setHasDecision] = useState(Boolean(storedConsent));
  const [settingsOpen, setSettingsOpen] = useState(false);

  const savePreferences = (nextPreferences) => {
    const normalized = {
      necessary: true,
      analytics: Boolean(nextPreferences.analytics),
      externalMedia: Boolean(nextPreferences.externalMedia),
    };

    setPreferences(normalized);
    setHasDecision(true);
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...normalized,
        version: CONSENT_VERSION,
        savedAt: new Date().toISOString(),
      })
    );
  };

  const value = {
    preferences,
    hasDecision,
    settingsOpen,
    acceptAll: () => {
      savePreferences({ analytics: true, externalMedia: true });
      setSettingsOpen(false);
    },
    rejectAll: () => {
      savePreferences({ analytics: false, externalMedia: false });
      setSettingsOpen(false);
    },
    savePreferences,
    enableExternalMedia: () =>
      savePreferences({ ...preferences, externalMedia: true }),
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent debe utilizarse dentro de ConsentProvider");
  }
  return context;
};

export { STORAGE_KEY };
